<template>
  <div class="training-snapshot" :class="{ 'is-wide': isWide }">
    <div class="snap-header">
      <h3>Training</h3>
      <a
        href="javascript:;"
        class="full-week-link"
        @click="$emit('openFullWeek')"
      >
        {{ isWide ? "Details & log" : "Full week" }}
        <i class="bi bi-chevron-right"></i>
      </a>
    </div>

    <!-- Wide screens: the whole week, one column per day -->
    <div v-if="isWide" class="snap-week">
      <div class="day-nav week-nav">
        <button
          class="day-arrow"
          :disabled="loadingWeek"
          @click="stepWeek(-1)"
          aria-label="Previous week"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <button
          class="day-center"
          @click="goToday"
          :title="isViewWeekCurrent ? 'This week' : 'Jump to this week'"
        >
          <span class="day-label">{{ weekRangeLabel }}</span>
          <span class="day-md">{{
            isViewWeekCurrent ? "This week" : "Back to this week"
          }}</span>
        </button>
        <button
          class="day-arrow"
          :disabled="loadingWeek"
          @click="stepWeek(1)"
          aria-label="Next week"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
      <div v-if="loadingWeek" class="snap-loading">Loading…</div>
      <TrainingWeekTimeline
        v-else
        :weekDays="weekDays"
        @openSession="$emit('openFullWeek')"
      />
    </div>

    <!-- Narrow screens: one day with back / forward navigation -->
    <div v-else class="snap-day">
      <div class="day-nav">
        <button
          class="day-arrow"
          :disabled="loadingWeek"
          @click="stepDay(-1)"
          aria-label="Previous day"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <button
          class="day-center"
          @click="goToday"
          :title="isViewToday ? 'Today' : 'Jump to today'"
        >
          <span class="day-label">{{ viewDayLabel }}</span>
          <span class="day-md" :class="{ 'is-match': viewDay.isMatchDay }">{{
            viewDay.mdLabel
          }}</span>
        </button>
        <button
          class="day-arrow"
          :disabled="loadingWeek"
          @click="stepDay(1)"
          aria-label="Next day"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>

      <div v-if="loadingWeek" class="snap-loading">Loading…</div>
      <div v-else-if="viewDaySessions.length" class="day-sessions">
        <SessionCard
          v-for="session in viewDaySessions"
          :key="session.id"
          :session="session"
          @openSession="$emit('openFullWeek')"
        />
      </div>
      <div v-else class="rest-day">Rest day</div>
    </div>

    <div class="snap-bottom">
      <!-- Season Overview (high level) -->
      <div class="snap-section">
        <h4><i class="bi bi-graph-up"></i> Season Overview</h4>
        <div class="snap-grid">
          <div class="snap-stat">
            <span class="snap-num">{{ seasonTotals.avgRpe || "–" }}</span>
            <span class="snap-lbl">Avg RPE</span>
          </div>
          <div class="snap-stat">
            <span class="snap-num">{{ seasonTotals.sessions }}</span>
            <span class="snap-lbl">Sessions</span>
          </div>
          <div class="snap-stat stat-green">
            <span class="snap-num"
              >{{ gamesAttendance.attended }}/{{ gamesAttendance.total }}</span
            >
            <span class="snap-lbl">Games</span>
          </div>
          <div class="snap-stat stat-green">
            <span class="snap-num"
              >{{ trainingAttendance.attended }}/{{
                trainingAttendance.total
              }}</span
            >
            <span class="snap-lbl">Training</span>
          </div>
        </div>
      </div>

      <!-- Session RPE Timeline -->
      <div v-if="sessionTimeline.length > 0" class="snap-section">
        <h4><i class="bi bi-activity"></i> Session RPE Timeline</h4>
        <div class="rpe-bar-chart">
          <div class="rpe-y-axis">
            <span>10</span><span>8</span><span>6</span><span>4</span
            ><span>2</span><span>0</span>
          </div>
          <div class="rpe-bars-area">
            <div class="rpe-grid-lines">
              <div
                class="rpe-grid-line"
                v-for="n in 5"
                :key="n"
                :style="{ bottom: n * 20 + '%' }"
              ></div>
            </div>
            <div class="rpe-bars-scroll">
              <div
                v-for="(session, idx) in sessionTimeline"
                :key="idx"
                class="rpe-bar-col"
                :title="`${session.created_date}\nRPE: ${session.effort}/10\n${sessionTypeLabel(session)}`"
              >
                <div class="rpe-bar-wrapper">
                  <div
                    class="rpe-bar"
                    :style="{
                      height: (parseInt(session.effort) / 10) * 100 + '%',
                      background: rpeBarGradient(session.effort),
                    }"
                  >
                    <span class="rpe-bar-value">{{ session.effort }}</span>
                  </div>
                </div>
                <div
                  class="rpe-bar-type-dot"
                  :style="{ background: dotColor(session) }"
                ></div>
                <span class="rpe-bar-date">{{
                  shortDate(session.created_date)
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="timeline-legend">
          <span class="legend-item"
            ><span class="legend-dot" style="background: #3b82f6"></span>
            Team</span
          >
          <span class="legend-item"
            ><span class="legend-dot" style="background: #22c55e"></span>
            Own</span
          >
          <span class="legend-item"
            ><span class="legend-dot" style="background: #f59e0b"></span>
            S&amp;C</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { TrainingWeek } from "@/services/training-week-service.js";
import SessionCard from "./SessionCard.vue";
import TrainingWeekTimeline from "./TrainingWeekTimeline.vue";

// Above this width the card is full-width under the stats columns and shows
// all seven days; below it falls back to the single-day stepper.
const WIDE_QUERY = "(min-width: 992px)";

export default {
  name: "ProfileTrainingSnapshot",
  components: { SessionCard, TrainingWeekTimeline },
  emits: ["openFullWeek"],
  props: {
    selectedUserData: { type: Object, default: () => ({}) },
    userData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loadingWeek: false,
      weekStartDate: null,
      weekEndDate: null,
      allSessions: [],
      matchDay: [],
      seasonData: {
        byType: [],
        weeklyLoad: [],
        sessionTimeline: [],
        attendance: {},
      },
      // The day currently shown in the "today" panel (YYYY-MM-DD).
      viewDate: moment().format("YYYY-MM-DD"),
      isWide: window.matchMedia ? window.matchMedia(WIDE_QUERY).matches : false,
      mediaQuery: null,
    };
  },
  computed: {
    teamId() {
      return this.selectedUserData.teamId || this.userData.teamId;
    },
    userId() {
      return this.selectedUserData.id || this.userData.id;
    },
    isViewToday() {
      return this.viewDate === moment().format("YYYY-MM-DD");
    },
    viewDayLabel() {
      const d = moment(this.viewDate);
      return this.isViewToday
        ? `Today · ${d.format("ddd D MMM")}`
        : d.format("ddd D MMM");
    },
    // Match dates within the loaded week (for MD labelling).
    matchDateSet() {
      const matches = Array.isArray(this.matchDay)
        ? this.matchDay
        : this.matchDay
          ? [this.matchDay]
          : [];
      const set = new Set(
        matches.map((m) => moment(m.matchDate).format("YYYY-MM-DD")),
      );
      this.allSessions
        .filter((s) => s.gameType !== "Training")
        .forEach((s) => set.add(moment(s.matchDate).format("YYYY-MM-DD")));
      return set;
    },
    viewDaySessions() {
      return this.allSessions.filter(
        (s) => moment(s.matchDate).format("YYYY-MM-DD") === this.viewDate,
      );
    },
    // MD label for the viewed day, relative to the nearest match in the week.
    viewDay() {
      return this.mdInfoFor(this.viewDate);
    },
    isViewWeekCurrent() {
      const today = moment().format("YYYY-MM-DD");
      return today >= this.weekStartDate && today <= this.weekEndDate;
    },
    weekRangeLabel() {
      if (!this.weekStartDate) return "";
      return `${moment(this.weekStartDate).format("D MMM")} – ${moment(
        this.weekEndDate,
      ).format("D MMM YYYY")}`;
    },
    // Seven day columns for the wide layout (same shape TrainingWeek builds).
    weekDays() {
      if (!this.weekStartDate) return [];
      const today = moment().format("YYYY-MM-DD");
      const start = moment(this.weekStartDate);
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = start.clone().add(i, "days");
        const dateStr = date.format("YYYY-MM-DD");
        const { mdLabel, isMatchDay } = this.mdInfoFor(dateStr);
        days.push({
          date: dateStr,
          dayName: date.format("ddd"),
          dateFormatted: date.format("D MMM"),
          isToday: dateStr === today,
          isMatchDay,
          mdLabel,
          sessions: this.allSessions.filter(
            (s) => moment(s.matchDate).format("YYYY-MM-DD") === dateStr,
          ),
        });
      }
      return days;
    },
    seasonTotals() {
      const byType = this.seasonData.byType || [];
      const getType = (t) =>
        byType.find(
          (x) => (x.training_session_type || "Team Training") === t,
        ) || {};
      const team = getType("Team Training");
      const own = getType("Own Training");
      const sc = getType("S&C");
      const avgValues = byType
        .filter((t) => t.avg_effort)
        .map((t) => parseFloat(t.avg_effort));
      const avgRpe = avgValues.length
        ? (avgValues.reduce((a, b) => a + b, 0) / avgValues.length).toFixed(1)
        : null;
      return {
        sessions:
          (team.sessions_attended || 0) +
          (own.sessions_attended || 0) +
          (sc.sessions_attended || 0),
        avgRpe,
      };
    },
    gamesAttendance() {
      const att = (this.seasonData.attendance || {}).games || {};
      return {
        total: parseInt(att.total) || 0,
        attended: parseInt(att.attended) || 0,
      };
    },
    trainingAttendance() {
      const att = (this.seasonData.attendance || {}).training || {};
      return {
        total: parseInt(att.total) || 0,
        attended: parseInt(att.attended) || 0,
      };
    },
    sessionTimeline() {
      return this.seasonData.sessionTimeline || [];
    },
  },
  watch: {
    selectedUserData: {
      handler() {
        this.viewDate = moment().format("YYYY-MM-DD");
        this.loadWeekFor(this.viewDate);
        this.loadSeason();
      },
      deep: true,
    },
  },
  created() {
    this.loadWeekFor(this.viewDate);
    this.loadSeason();
  },
  mounted() {
    if (!window.matchMedia) return;
    this.mediaQuery = window.matchMedia(WIDE_QUERY);
    this.onMediaChange = (e) => {
      this.isWide = e.matches;
    };
    this.mediaQuery.addEventListener("change", this.onMediaChange);
  },
  beforeUnmount() {
    if (this.mediaQuery && this.onMediaChange) {
      this.mediaQuery.removeEventListener("change", this.onMediaChange);
    }
  },
  methods: {
    // MD / MD±n label for a date, relative to the nearest match in the week.
    mdInfoFor(dateStr) {
      const set = this.matchDateSet;
      if (set.has(dateStr)) return { mdLabel: "MD", isMatchDay: true };
      const sorted = [...set].sort();
      if (!sorted.length) return { mdLabel: "", isMatchDay: false };
      let nearest = sorted[0];
      let min = Math.abs(moment(dateStr).diff(moment(nearest), "days"));
      for (const md of sorted) {
        const dist = Math.abs(moment(dateStr).diff(moment(md), "days"));
        if (dist < min) {
          min = dist;
          nearest = md;
        }
      }
      const diff = moment(dateStr).diff(moment(nearest), "days");
      return {
        mdLabel: diff > 0 ? `MD+${diff}` : `MD${diff}`,
        isMatchDay: false,
      };
    },
    async loadWeekFor(date) {
      if (!this.teamId || !this.userId) return;
      this.weekStartDate = moment(date).startOf("isoWeek").format("YYYY-MM-DD");
      this.weekEndDate = moment(date).endOf("isoWeek").format("YYYY-MM-DD");
      this.loadingWeek = true;
      try {
        const [weekRes, matchRes] = await Promise.all([
          TrainingWeek.getWeekData({
            teamId: this.teamId,
            userId: this.userId,
            weekStartDate: this.weekStartDate,
            weekEndDate: this.weekEndDate,
          }),
          TrainingWeek.getMatchDay({
            teamId: this.teamId,
            weekStartDate: this.weekStartDate,
            weekEndDate: this.weekEndDate,
          }),
        ]);
        this.allSessions = Array.isArray(weekRes.data) ? weekRes.data : [];
        this.matchDay = matchRes.data || [];
      } catch (e) {
        this.allSessions = [];
        this.matchDay = [];
      } finally {
        this.loadingWeek = false;
      }
    },
    async loadSeason() {
      if (!this.teamId || !this.userId) return;
      const seasonStart =
        localStorage.getItem("currentPlayingseasonStartDate") ||
        moment().subtract(6, "months").format("YYYY-MM-DD");
      const seasonEnd =
        localStorage.getItem("currentPlayingseasonEndDate") ||
        moment().add(6, "months").format("YYYY-MM-DD");
      try {
        const res = await TrainingWeek.getSeasonStats({
          teamId: this.teamId,
          userId: this.userId,
          season_start_date: seasonStart,
          season_end_date: seasonEnd,
        });
        this.seasonData = res.data || {
          byType: [],
          weeklyLoad: [],
          sessionTimeline: [],
          attendance: {},
        };
      } catch (e) {
        // keep defaults
      }
    },
    // Step a day; reload the week only when crossing its boundary.
    stepDay(delta) {
      const next = moment(this.viewDate)
        .add(delta, "days")
        .format("YYYY-MM-DD");
      this.viewDate = next;
      if (next < this.weekStartDate || next > this.weekEndDate) {
        this.loadWeekFor(next);
      }
    },
    // Wide layout: move a whole week; keep viewDate inside it so switching
    // back to the narrow layout lands on the same week.
    stepWeek(delta) {
      const next = moment(this.weekStartDate)
        .add(delta * 7, "days")
        .format("YYYY-MM-DD");
      this.viewDate = next;
      this.loadWeekFor(next);
    },
    goToday() {
      const today = moment().format("YYYY-MM-DD");
      this.viewDate = today;
      if (today < this.weekStartDate || today > this.weekEndDate) {
        this.loadWeekFor(today);
      }
    },
    rpeBarGradient(effort) {
      const n = parseInt(effort);
      if (n <= 2) return "linear-gradient(180deg, #93c5fd, #3b82f6)";
      if (n <= 4) return "linear-gradient(180deg, #86efac, #22c55e)";
      if (n <= 6) return "linear-gradient(180deg, #fde047, #eab308)";
      if (n <= 8) return "linear-gradient(180deg, #fdba74, #f97316)";
      return "linear-gradient(180deg, #fca5a5, #ef4444)";
    },
    dotColor(session) {
      const type =
        session.training_session_type ||
        session.game_session_type ||
        "Team Training";
      if (type === "Own Training") return "#22c55e";
      if (type === "S&C") return "#f59e0b";
      return "#3b82f6";
    },
    sessionTypeLabel(session) {
      return (
        session.training_session_type ||
        session.game_session_type ||
        "Team Training"
      );
    },
    shortDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    },
  },
};
</script>

<style scoped lang="scss">
.training-snapshot {
  margin-top: 1.25rem;
  padding: 1rem 1.1rem 1.1rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 10px;
}

.snap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;

  h3 {
    margin: 0;
    font-size: 1.05rem;
  }

  .full-week-link {
    font-size: 0.8rem;
    font-weight: 600;
    color: #3b82f6;
    cursor: pointer;
    white-space: nowrap;

    i {
      font-size: 0.7rem;
    }
  }
}

/* Today panel */
.day-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.day-arrow {
  flex: 0 0 auto;
  width: 40px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 1rem;
  line-height: 1;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 8px;
  color: #4b5563;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #eef2f7;
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

.day-center {
  flex: 1;
  height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 0 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 8px;
  cursor: pointer;

  .day-label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #1f2937;
  }
  .day-md {
    font-size: 0.62rem;
    font-weight: 700;
    color: #6b7280;
    letter-spacing: 0.3px;

    &.is-match {
      color: #ef4444;
    }
  }
}

.day-sessions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rest-day,
.snap-loading {
  padding: 14px;
  text-align: center;
  font-size: 0.85rem;
  font-style: italic;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 8px;
}

/* Condensed season overview */
.snap-section {
  margin-top: 14px;

  h4 {
    margin: 0 0 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #4b5563;
    display: flex;
    align-items: center;
    gap: 5px;

    i {
      color: #3b82f6;
      font-size: 0.85rem;
    }
  }
}

.snap-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.snap-stat {
  text-align: center;
  padding: 9px 6px;
  background: #f9fafb;
  border-radius: 8px;

  .snap-num {
    display: block;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1f2937;
  }
  .snap-lbl {
    display: block;
    font-size: 0.62rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    margin-top: 2px;
  }

  &.stat-green {
    background: #f0fdf4;
    .snap-num {
      color: #16a34a;
    }
  }
}

/* RPE timeline (mirrors TrainingWeekStats) */
.rpe-bar-chart {
  display: flex;
  gap: 4px;
  height: 150px;
}

.rpe-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 4px 28px 0;
  width: 20px;
  flex-shrink: 0;

  span {
    font-size: 0.58rem;
    color: #9ca3af;
    text-align: right;
    line-height: 1;
  }
}

.rpe-bars-area {
  flex: 1;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.rpe-grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 28px;
  pointer-events: none;
}

.rpe-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #f0f0f0;
}

.rpe-bars-scroll {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 100%;
  min-width: min-content;
  padding-bottom: 28px;
}

.rpe-bar-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30px;
  flex-shrink: 0;
  height: 100%;
  position: relative;
}

.rpe-bar-wrapper {
  flex: 1;
  display: flex;
  align-items: flex-end;
  width: 100%;
}

.rpe-bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 0.3s ease;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.rpe-bar-value {
  font-size: 0.58rem;
  font-weight: 800;
  color: #fff;
  padding-top: 3px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.rpe-bar-type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 3px;
  flex-shrink: 0;
}

.rpe-bar-date {
  font-size: 0.5rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-top: 2px;
}

.timeline-legend {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62rem;
  color: #6b7280;
  font-weight: 500;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

/* Wide layout: full week grid on top, season stats and RPE timeline side by
   side underneath. */
.is-wide {
  .week-nav {
    max-width: 420px;
    margin: 0 auto 12px;
  }

  .snap-bottom {
    display: grid;
    grid-template-columns: minmax(300px, 1fr) minmax(0, 2fr);
    gap: 1.5rem 2.5rem;
    align-items: start;
    margin-top: 6px;
    padding-top: 10px;
    border-top: 1px solid #f1f3f5;
  }

  .snap-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .rpe-bar-chart {
    height: 190px;
  }

  .rpe-bar-col {
    width: 36px;
  }
}

@media (min-width: 1400px) {
  .is-wide .snap-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
